import React from 'react';
import { Link } from 'react-router-dom';
import useSEO from '../../hooks/useSEO';
import AnswerCapsule from '../../components/AnswerCapsule';
import { blogAuthor } from '../../utils/blogJsonLd';
import './hubs/VehicleHub.css';
import './TPermitHub.css';
// The guide list is shared with BlogPost.js and the build injector, so member
// posts can link back up to this hub — see data/tpermitGuides.js.
import { CLUSTERS } from '../../data/tpermitGuides';

const BASE_URL = 'https://www.buyselltaxi.com';
const PAGE_URL = `${BASE_URL}/t-permit`;

// Shown in the hero and emitted as datePublished/dateModified. Bump UPDATED
// whenever the guide list or copy changes — permit rules go stale, and an
// undated regulatory page gives neither readers nor AI answer engines any way
// to judge whether it is still current.
const PUBLISHED = '2026-08-24';
const UPDATED = '2026-09-07';
const fmtDate = (iso) => new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' });


const ANSWER =
  'A T-permit is a transport permit issued by a State Transport Authority under the Motor Vehicles Act that allows a vehicle to be used commercially — to carry passengers for hire. Vehicles holding one are registered in the transport series and carry a yellow number plate with black lettering. The everyday taxi and app-cab version is a contract carriage permit, meaning the whole vehicle is hired under a single contract for one journey rather than picking up separate paying passengers along the way.';

const TPermitHub = () => {
  const allPosts = CLUSTERS.flatMap(c => c.posts);

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: BASE_URL },
        { '@type': 'ListItem', position: 2, name: 'T-Permit Guide', item: PAGE_URL },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: 'T-Permit Cars in India: The Complete Guide',
      description:
        'What a T-permit is, what it costs, how to get one, how to convert a private car, and how permit status affects what a commercial vehicle is worth.',
      about: { '@type': 'Thing', name: 'T-Permit (commercial transport permit, India)' },
      mainEntityOfPage: { '@type': 'WebPage', '@id': PAGE_URL },
      url: PAGE_URL,
      inLanguage: 'en-IN',
      datePublished: PUBLISHED,
      dateModified: UPDATED,
      author: blogAuthor({}),
      publisher: {
        '@type': 'Organization',
        name: 'Buy Sell Taxi',
        url: BASE_URL,
        logo: { '@type': 'ImageObject', url: `${BASE_URL}/logo192.png` },
      },
    },
    // An ItemList of the library so an AI crawler can enumerate the guides
    // without executing JavaScript. Deliberately no FAQPage: Google retired FAQ
    // rich results for all sites on 7 May 2026, so new FAQ markup earns nothing.
    {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: 'T-Permit guides',
      url: PAGE_URL,
      numberOfItems: allPosts.length,
      itemListElement: allPosts.map(([slug, title], i) => ({
        '@type': 'ListItem',
        position: i + 1,
        url: `${BASE_URL}/blog/${slug}`,
        name: title,
      })),
    },
  ];

  useSEO({
    image: `${BASE_URL}/og/landing/t-permit.png`,
    // useSEO appends ' | Buy Sell Taxi' (16 chars), so keep the base short
    // enough that the full title stays inside the ~60-char SERP budget.
    title: 'T-Permit Cars in India — Rules, Cost & Guide',
    description:
      'What a T-permit is, what it costs, how to get one, and how to convert a private car. Plus verified T-permit taxis for sale in Delhi NCR.',
    keywords:
      'T permit car India, what is T permit, T permit cost, yellow number plate rules, commercial vehicle permit India, contract carriage permit, convert private car to commercial, AITP all India tourist permit',
    url: PAGE_URL,
    type: 'article',
    jsonLd,
  });

  return (
    <div className="hub-page tpermit-hub">

      <section className="hub-hero">
        <div className="hub-hero-inner">
          <nav className="hub-breadcrumb">
            <Link to="/">Home</Link> <span>/</span>
            <span>T-Permit Guide</span>
          </nav>
          <h1>T-Permit Cars in India: The Complete Guide</h1>
          <p className="tpermit-standfirst">
            Everything we know about commercial permits, in one place — and the vehicles that already hold one.
          </p>
          <p className="tpermit-byline">
            By the Buy Sell Taxi team · Updated <time dateTime={UPDATED}>{fmtDate(UPDATED)}</time>
          </p>
        </div>
      </section>

      <div className="tpermit-body">

        <AnswerCapsule
          question="What is a T-permit car in India?"
          answer={ANSWER}
        />

        <section className="hub-section tpermit-intro">
          <p>
            Permit status is the single most consequential fact about a commercial vehicle, and the
            hardest to check. It decides where the vehicle may legally operate, what it costs to run,
            whether an aggregator will onboard it — and a large share of what it is worth on resale.
          </p>
          <p>
            We have written {allPosts.length} guides on it. They are organised below by what you are
            actually trying to do, rather than by regulation.
          </p>
        </section>

        {/* The commercial bridge, placed before the library rather than after it.
            Readers arriving on a permit question are the closest thing we have to
            qualified demand, and this is the only link on the page that turns a
            regulatory search into an enquiry. */}
        <section className="hub-section tpermit-bridge">
          <h2>Looking for a vehicle that already has one?</h2>
          <p>
            Buying a vehicle with a valid permit already in place avoids the conversion process
            described below entirely. Every listing on Buy Sell Taxi shows its RC and permit status
            before you contact the seller.
          </p>
          <div className="tpermit-bridge-links">
            <Link to="/vehicles" className="hub-cta-btn">Browse verified taxis</Link>
            <Link to="/buy-taxi-delhi" className="tpermit-bridge-secondary">Permit taxis in Delhi</Link>
            <Link to="/buy-taxi-gurugram" className="tpermit-bridge-secondary">Permit taxis in Gurugram</Link>
          </div>
        </section>

        {CLUSTERS.map(cluster => (
          <section key={cluster.id} id={cluster.id} className="hub-section tpermit-cluster">
            <h2>{cluster.heading}</h2>
            <p className="tpermit-blurb">{cluster.blurb}</p>
            <ul className="tpermit-links">
              {cluster.posts.map(([slug, title]) => (
                <li key={slug}>
                  <Link to={`/blog/${slug}`}>{title}</Link>
                </li>
              ))}
            </ul>
          </section>
        ))}

        <section className="hub-section tpermit-states">
          <h2>Permit rules by state</h2>
          <p className="tpermit-blurb">
            Permits are issued by the state, not centrally — the cost, the paperwork and the
            age ceiling all change at a border most NCR operators cross every day. These cover
            the three states where our stock actually sits.
          </p>
          <ul className="tpermit-links">
            <li><Link to="/t-permit/by-state/delhi">T-permit rules in Delhi</Link></li>
            <li><Link to="/t-permit/by-state/haryana">T-permit rules in Haryana (Gurugram, Faridabad)</Link></li>
            <li><Link to="/t-permit/by-state/uttar-pradesh">T-permit rules in Uttar Pradesh (Noida, Ghaziabad)</Link></li>
          </ul>
        </section>

        <section className="hub-section tpermit-sell">
          <h2>Selling a vehicle that holds a permit</h2>
          <p>
            A valid, transferable permit is usually the difference between a quick sale and a stalled
            one — buyers pay for the paperwork they do not have to redo. If you are selling, list it
            free and we will surface the permit status to buyers.
          </p>
          <div className="tpermit-bridge-links">
            <Link to="/sell-taxi" className="hub-cta-btn">List your taxi free</Link>
            <Link to="/taxi-valuation" className="tpermit-bridge-secondary">What is my taxi worth?</Link>
          </div>
        </section>

      </div>
    </div>
  );
};

export default TPermitHub;
